<template v-slot:default>
  <div>
    <toolbarTitle :title="`Passengers (${passengers ? passengers.length : 0})`" />

    <div class="elevation-1 rounded passengers-table-wrapper">
      <v-data-table
        :headers="computedHeaders"
        :items="passengers"
        :items-per-page="-1"
        disable-pagination
        hide-default-footer>
        <template v-slot:[`item.firstName`]="{ item }">
          <div class="font-weight-bold">{{ item.firstName }} {{ item.lastName }}</div>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <div class="font-weight-bold">
            {{ item.email ? item.email : "N/A" }}
          </div>
        </template>
        <template v-slot:[`item.phone`]="{ item }">
          <div class="font-weight-bold">
            {{ item.phone ? item.phone : "N/A" }}
          </div>
        </template>
        <template v-slot:[`item.birthdate`]="{ item }">
          <div class="font-weight-bold">
            {{ item.birthdate ? item.birthdate : "N/A" }}
          </div>
        </template>
        <template v-slot:[`item.countryOfResidence`]="{ item }">
          <div class="font-weight-bold">
            {{ item.countryOfResidence ? item.countryOfResidence : "N/A" }}
          </div>
        </template>
        <template v-if="canBeEdited" v-slot:[`item.actions`]="{ item, index }">
          <v-btn icon @click="openEditModal(item, index)">
            <v-icon size="20" color="third">mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="editDialog" max-width="600px" :scrollable="false" :persistent="loading">
      <v-card>
        <v-toolbar fixed color="primary" class="title" dense dark>
          <v-toolbar-title class="ml-2">Edit Passenger</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :disabled="loading" @click="editDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5 modal-content">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editedPassenger.firstName"
                label="First Name"
                outlined
                dense
                hide-details="auto"
                clearable
                :rules="validate.required" />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedPassenger.lastName"
                label="Last Name"
                outlined
                dense
                hide-details="auto"
                clearable
                :rules="validate.required" />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedPassenger.email"
                label="E-Mail"
                outlined
                dense
                hide-details="auto"
                :rules="validate.email"
                clearable />
            </v-col>
            <v-col cols="12">
              <VuePhoneNumberInput
                :default-country-code="selectedCountryCode"
                @update="logPhone"
                :error="editedPassenger.phone && !phoneValid"
                v-model="editedPassenger.phone"
                :clearable="true" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="mt-3 pt-0">
              <div class="info-message d-flex align-center pa-3">
                <v-icon color="third" class="mr-2">mdi-information-outline</v-icon>
                <p class="mb-0 text-body-2 grey--text text--darken-2">
                  This will update passenger information on all systems, including the tickets.
                </p>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="loading" @click="editDialog = false">Cancel</v-btn>
          <v-btn color="accent" :loading="loading" :disabled="!isFormValid" @click="savePassenger">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import toolbarTitle from "@/components/common/toolbarTitle";
import Trainplanet from "@/util/trainplanet.api";
import { validator } from "@/util/helpers";
import { parsePhone } from "@/util/phone";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";

export default {
  name: "Passengers",
  components: {
    toolbarTitle,
    VuePhoneNumberInput,
  },
  props: {
    passengers: Array,
    canBeEdited: {
      type: Boolean,
      required: false,
      default: false,
    },
    orderId: {
      type: String,
      required: false,
      default: null,
    },
  },

  mounted() {
    this.passengersData = this.passengers;
  },
  data: () => ({
    selectedCountryCode: "SE",
    passengersData: [],
    editDialog: false,
    loading: false,
    phoneValid: false,
    formattedPhone: "",
    validate: {
      required: validator.required(),
      email: validator.email(),
    },
    editedPassenger: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    defaultPassenger: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    headers: [
      {
        text: "Full Name",
        value: "firstName",
        sortable: false,
      },
      {
        text: "E-Mail",
        value: "email",
        sortable: false,
      },
      {
        text: "Phone",
        value: "phone",
        sortable: false,
      },
      {
        text: "Birthdate",
        value: "birthdate",
        sortable: false,
      },
      {
        text: "Country",
        value: "countryOfResidence",
        sortable: false,
      },
    ],
  }),
  computed: {
    computedHeaders() {
      if (!this.canBeEdited) return this.headers;
      return [...this.headers, { text: "Action", value: "actions", sortable: false, align: "right" }];
    },
    isFormValid() {
      const requiredOk = (v) => this.validate.required.every((rule) => rule(v) === true);
      const emailOk = this.validate.email.every((rule) => rule(this.editedPassenger.email) === true);
      const phoneOk = this.editedPassenger.phone && this.phoneValid;
      return (
        requiredOk(this.editedPassenger.firstName) && requiredOk(this.editedPassenger.lastName) && emailOk && phoneOk
      );
    },
  },
  methods: {
    logPhone(payload) {
      this.selectedCountryCode = payload.countryCode;
      this.formattedPhone = payload.formatInternational || "";
      // The component emits `update` before `input`, so editedPassenger.phone is still the
      // previous value here. payload.phoneNumber is the current one and is unset while empty.
      this.phoneValid = payload.phoneNumber ? payload.isValid : false;
    },

    openEditModal(passenger) {
      const parsedPhone = parsePhone(passenger.phone);

      // On every open after the first the input is already mounted and will not re-emit for a
      // national number, so logPhone does not run — an unparseable stored number has to be
      // marked invalid here, otherwise the save writes the raw value back.
      this.phoneValid = !!parsedPhone;
      this.formattedPhone = parsedPhone ? parsedPhone.formatted : passenger.phone || "";
      this.selectedCountryCode = parsedPhone ? parsedPhone.countryCode : "SE";
      this.editedPassenger = {
        id: passenger.id,
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        email: passenger.email,
        phone: parsedPhone ? parsedPhone.nationalNumber : passenger.phone,
      };
      this.editDialog = true;
    },

    closeDialog() {
      this.editDialog = false;
      this.$nextTick(() => {
        this.editedPassenger = Object.assign({}, this.defaultPassenger);
        this.phoneValid = false;
      });
    },

    async savePassenger() {
      if (!this.orderId) {
        this.$store.dispatch("error", "Order ID is required");
        return;
      }

      this.loading = true;

      try {
        const payload = {
          firstName: this.editedPassenger.firstName,
          lastName: this.editedPassenger.lastName,
          email: this.editedPassenger.email,
          phone: this.formattedPhone,
        };

        await Trainplanet.updateOrderPassengers(this.orderId, this.editedPassenger.id, payload);

        this.$store.dispatch("success", "Passenger updated successfully");
        this.closeDialog();

        this.$emit("passenger-updated");
      } catch (error) {
        this.$store.dispatch("error", error.response?.data?.message || "Failed to update passenger");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style>
.passengers-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
}

.select-country-container:hover .country-selector__input {
  border-color: black !important;
  box-shadow: none !important;
}

.modal-content {
  overflow-y: visible !important;
  overflow-x: hidden !important;
}

.info-message {
  background-color: #f5f5f5;
  border-radius: 4px;
}
</style>
